/**
 * Locale Formatting Utilities
 *
 * Provides formatting functions for dates, numbers, and currencies
 * based on organization locale settings.
 *
 * Usage:
 *   import { formatDate, formatNumber, formatCurrency } from '@/lib/platform/utils/locale-formatting';
 *   formatDate(new Date(), 'DD-MM-YYYY', 'da'); // "04-12-2025"
 *   formatNumber(1234567.89, 'eu', 2); // "1.234.567,89"
 *   formatCurrency(1234.56, 'DKK', 'eu', 2); // "1.234,56 kr."
 */

import { logger } from '../../platform/utils/logger';
import { format as formatDateFns, toZonedTime } from 'date-fns-tz';
import { da, enUS } from 'date-fns/locale';

// Type definitions
export type DateFormatType = 'DD-MM-YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
export type NumberFormatType = 'eu' | 'us' | 'fr';
export type LanguageType = 'da' | 'en';

// Currency symbols mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  DKK: 'kr.',
  EUR: '€',
  USD: '$',
  GBP: '£',
  SEK: 'kr',
  NOK: 'kr',
};

// Date-fns locale mapping
const DATE_LOCALES = {
  da: da,
  en: enUS,
};

/**
 * Format a date according to specified format and language
 *
 * @param date - Date to format
 * @param dateFormat - Format type (DD-MM-YYYY, MM/DD/YYYY, YYYY-MM-DD)
 * @param language - Language for month/day names (da, en)
 * @returns Formatted date string
 *
 * @example
 * formatDate(new Date('2025-12-04'), 'DD-MM-YYYY', 'da') // "04-12-2025"
 * formatDate(new Date('2025-12-04'), 'MM/DD/YYYY', 'en') // "12/04/2025"
 */
export function formatDate(
  date: Date | string | null | undefined,
  dateFormat: DateFormatType = 'DD-MM-YYYY',
  language: LanguageType = 'en'
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check for invalid date
  if (isNaN(dateObj.getTime())) return '';

  const locale = DATE_LOCALES[language];

  // Map format to date-fns format string
  const formatMap: Record<DateFormatType, string> = {
    'DD-MM-YYYY': 'dd-MM-yyyy',
    'MM/DD/YYYY': 'MM/dd/yyyy',
    'YYYY-MM-DD': 'yyyy-MM-dd',
  };

  const formatString = formatMap[dateFormat];

  try {
    return formatDateFns(dateObj, formatString, { locale });
  } catch (error) {
    logger.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format a number according to specified number format
 *
 * @param value - Number to format
 * @param numberFormat - Format type (eu, us, fr)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234567.89, 'eu', 2) // "1.234.567,89"
 * formatNumber(1234567.89, 'us', 2) // "1,234,567.89"
 * formatNumber(1234567.89, 'fr', 2) // "1 234 567,89"
 */
export function formatNumber(
  value: number | null | undefined,
  numberFormat: NumberFormatType = 'eu',
  decimals: number = 2
): string {
  if (value === null || value === undefined || isNaN(value)) return '';

  // Round to specified decimals
  const rounded = Number(value.toFixed(decimals));

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = rounded.toFixed(decimals).split('.');

  // Format based on number format type
  let formattedInteger: string;
  let decimalSeparator: string;

  switch (numberFormat) {
    case 'eu': // European: 1.234.567,89
      formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      decimalSeparator = ',';
      break;

    case 'us': // US/UK: 1,234,567.89
      formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      decimalSeparator = '.';
      break;

    case 'fr': // French/Swedish: 1 234 567,89
      formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      decimalSeparator = ',';
      break;

    default:
      formattedInteger = integerPart;
      decimalSeparator = '.';
  }

  // Return formatted number with decimals
  return decimals > 0 ? `${formattedInteger}${decimalSeparator}${decimalPart}` : formattedInteger;
}

/**
 * Format a currency value with proper symbol and number formatting
 *
 * @param value - Amount to format
 * @param currency - Currency code (DKK, EUR, USD, etc.)
 * @param numberFormat - Number format type (eu, us, fr)
 * @param decimals - Number of decimal places (default: 0 for whole numbers)
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1234.56, 'DKK', 'eu') // "1.235 kr." (rounded, no decimals)
 * formatCurrency(1234.56, 'DKK', 'eu', 2) // "1.234,56 kr." (with decimals)
 * formatCurrency(1234.56, 'USD', 'us') // "$1,235" (rounded, no decimals)
 * formatCurrency(1234.56, 'EUR', 'fr', 2) // "1 234,56 €" (with decimals)
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string = 'DKK',
  numberFormat: NumberFormatType = 'eu',
  decimals: number = 0
): string {
  if (value === null || value === undefined || isNaN(value)) return '';

  const formattedNumber = formatNumber(value, numberFormat, decimals);
  const symbol = CURRENCY_SYMBOLS[currency.toUpperCase()] || currency;

  // For European format, put symbol after number
  // For US format, put symbol before number
  if (numberFormat === 'us' && (currency === 'USD' || currency === 'GBP')) {
    return `${symbol}${formattedNumber}`;
  } else {
    return `${formattedNumber} ${symbol}`;
  }
}

/**
 * Format a timestamp with date, time, and timezone
 *
 * @param date - Date to format
 * @param dateFormat - Date format type
 * @param language - Language for month/day names
 * @param timezone - IANA timezone (e.g., "Europe/Copenhagen")
 * @param includeTime - Whether to include time (default: true)
 * @returns Formatted timestamp string
 *
 * @example
 * formatTimestamp(new Date(), 'DD-MM-YYYY', 'da', 'Europe/Copenhagen', true)
 * // "04-12-2025 14:30"
 */
export function formatTimestamp(
  date: Date | string | null | undefined,
  dateFormat: DateFormatType = 'DD-MM-YYYY',
  language: LanguageType = 'en',
  timezone: string = 'Europe/Copenhagen',
  includeTime: boolean = true
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check for invalid date
  if (isNaN(dateObj.getTime())) return '';

  try {
    // Convert to target timezone
    const zonedDate = toZonedTime(dateObj, timezone);

    // Format date part
    const datePart = formatDate(zonedDate, dateFormat, language);

    if (!includeTime) {
      return datePart;
    }

    // Format time part (always HH:mm for consistency)
    const locale = DATE_LOCALES[language];
    const timePart = formatDateFns(zonedDate, 'HH:mm', { locale });

    return `${datePart} ${timePart}`;
  } catch (error) {
    logger.error('Error formatting timestamp:', error);
    return '';
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 *
 * @param date - Date to format
 * @param language - Language for text
 * @returns Relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000), 'en') // "1 hour ago"
 * formatRelativeTime(new Date(Date.now() - 86400000), 'da') // "1 dag siden"
 */
export function formatRelativeTime(
  date: Date | string | null | undefined,
  language: LanguageType = 'en'
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check for invalid date
  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  const translations = {
    en: {
      justNow: 'Just now',
      secondsAgo: (n: number) => `${n} ${n === 1 ? 'second' : 'seconds'} ago`,
      minutesAgo: (n: number) => `${n} ${n === 1 ? 'minute' : 'minutes'} ago`,
      hoursAgo: (n: number) => `${n} ${n === 1 ? 'hour' : 'hours'} ago`,
      daysAgo: (n: number) => `${n} ${n === 1 ? 'day' : 'days'} ago`,
      weeksAgo: (n: number) => `${n} ${n === 1 ? 'week' : 'weeks'} ago`,
      monthsAgo: (n: number) => `${n} ${n === 1 ? 'month' : 'months'} ago`,
      yearsAgo: (n: number) => `${n} ${n === 1 ? 'year' : 'years'} ago`,
    },
    da: {
      justNow: 'Lige nu',
      secondsAgo: (n: number) => `${n} ${n === 1 ? 'sekund' : 'sekunder'} siden`,
      minutesAgo: (n: number) => `${n} ${n === 1 ? 'minut' : 'minutter'} siden`,
      hoursAgo: (n: number) => `${n} ${n === 1 ? 'time' : 'timer'} siden`,
      daysAgo: (n: number) => `${n} ${n === 1 ? 'dag' : 'dage'} siden`,
      weeksAgo: (n: number) => `${n} ${n === 1 ? 'uge' : 'uger'} siden`,
      monthsAgo: (n: number) => `${n} ${n === 1 ? 'måned' : 'måneder'} siden`,
      yearsAgo: (n: number) => `${n} ${n === 1 ? 'år' : 'år'} siden`,
    },
  };

  const t = translations[language];

  if (diffSec < 10) return t.justNow;
  if (diffSec < 60) return t.secondsAgo(diffSec);
  if (diffMin < 60) return t.minutesAgo(diffMin);
  if (diffHour < 24) return t.hoursAgo(diffHour);
  if (diffDay < 7) return t.daysAgo(diffDay);
  if (diffWeek < 4) return t.weeksAgo(diffWeek);
  if (diffMonth < 12) return t.monthsAgo(diffMonth);
  return t.yearsAgo(diffYear);
}

/**
 * Parse a formatted number string back to a number
 *
 * @param value - Formatted number string
 * @param numberFormat - Format type used
 * @returns Parsed number or null if invalid
 *
 * @example
 * parseNumber("1.234,56", "eu") // 1234.56
 * parseNumber("1,234.56", "us") // 1234.56
 */
export function parseNumber(value: string, numberFormat: NumberFormatType = 'eu'): number | null {
  if (!value || typeof value !== 'string') return null;

  let normalized: string;

  switch (numberFormat) {
    case 'eu': // 1.234,56 -> 1234.56
      normalized = value.replace(/\./g, '').replace(',', '.');
      break;

    case 'us': // 1,234.56 -> 1234.56
      normalized = value.replace(/,/g, '');
      break;

    case 'fr': // 1 234,56 -> 1234.56
      normalized = value.replace(/\s/g, '').replace(',', '.');
      break;

    default:
      normalized = value;
  }

  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? null : parsed;
}
